import React, { memo, useMemo, useEffect } from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  Container,
  Stack,
  Box,
  Image,
  VStack,
  Flex,
  Text,
  Grid,
  HStack,
  useDisclosure,
} from "@chakra-ui/react"

import { useShopifyVariants } from "@app/hooks/useShopify"
import { useConfigContext } from "@app/providers/config"
import { useMedia } from "@app/hooks/useMedia"
import { SelectableOption } from "@app/components/Product/Form/ProductFormTypes"
import { ProductGiftcardForm } from "./ProductGiftcardForm"
import { ProductGiftcardToggle } from "./ProductGiftcardToggle"
import { ProductGiftcardBalance } from "@app/components/Product/Giftcard/Balance/ProductGiftcardBalance"
import { ProductGiftCardDenominations } from "@app/components/Product/Giftcard/ProductGiftCardDenominations"
import { ProductGiftCardDesigns } from "@app/components/Product/Giftcard/ProductGiftCardDesigns"
import { RecentlyViewed } from "@app/components/Product/Recent/RecentlyViewed"

import type { ProductProps } from "@root/types/global"

type Props = {
  handleErrors?: () => void
  isLoading?: boolean
  product: ProductProps
}

export const ProductGiftcard: React.FC<Props> = memo(({ handleErrors, isLoading, product }) => {
  const { giftcard } = useStaticQuery<GatsbyTypes.ProductGiftcardQuery>(graphql`
    query ProductGiftcard {
      giftcard: sanityPageGiftcard {
        additionalTermsContent
        additionalTermsTitle
        designOptions
        giftcardDescription
        step1
        step2
        title
      }
    }
  `)
  const {
    settings: {
      product: { giftCardDenomination, giftCardDesign },
    },
  } = useConfigContext()
  const { isTouch } = useMedia()
  const { isOpen, onToggle } = useDisclosure()
  const { handleOption, selectedOptions, activeVariant } = useShopifyVariants({
    firstAvailable: product?.variants?.length === 1,
    loading: isLoading,
    product,
    useParameter: true,
  })

  const selectableOptions: SelectableOption[] = useMemo(() => {
    return product.options
      .filter(({ values }) => values?.length > 1)
      .map(({ name, values }) => ({
        name,
        values: values.map(value => {
          const variant = product.variants.find(variant =>
            variant.selectedOptions.every(selectedOption => {
              const selectedValue = selectedOptions.find(opt => opt.name === selectedOption.name)?.value
              return selectedOption.name === name ? selectedOption.value === value : selectedOption.value === selectedValue
            })
          )
          return { variant, value }
        }),
      }))
  }, [product.options, product.variants, selectedOptions])

  const giftCardDesignOptions = selectableOptions.find(({ name }) => name === giftCardDesign)

  if (giftCardDesignOptions?.values && giftcard?.designOptions) {
    const { designOptions } = giftcard

    giftCardDesignOptions.values = giftCardDesignOptions.values
      .filter(({ value }) => designOptions.includes(value))
      .sort((a, b) => designOptions.indexOf(a.value) - designOptions.indexOf(b.value))
  }

  useEffect(() => {
    const setDefaultOption = (optionName: string) => {
      const selectedValue = selectedOptions.find(option => option.name === optionName)?.value
      const firstValue = selectableOptions.find(option => option.name === optionName)?.values[0]?.value

      if (!selectedValue && firstValue) {
        handleOption({ name: optionName, value: firstValue })
      }
    }
    setDefaultOption(giftCardDenomination)
    setDefaultOption(giftCardDesign)
  }, [selectableOptions, selectedOptions, giftCardDenomination, giftCardDesign, handleOption])

  return (
    <>
      <Container px={{ base: 0, xl: 12.5 }} pb={[12]} maxWidth="100%" paddingInlineStart="0 !important" paddingInlineEnd="0 !important">
        <Stack as="section" flexDirection={["column", "row"]} spacing={[8, 0]}>
          <Box w={["full", "50%"]} bg="blackAlpha.100" padding={[14, 20, 28]} display={["none", "block"]} height="fit-content">
            <Image src={activeVariant?.image?.src || activeVariant?.image?.originalSrc} alt="" width="100%" height="auto" />
          </Box>
          <VStack
            justifyContent={{ base: "center", sm: "flex-start", md: "flex-start" }}
            pt={{ base: 0, sm: 30 }}
            spacing={11.5}
            w={["full", "50%"]}
            alignItems="center"
          >
            <Flex w={["full", 350, 432]} gap={6} direction="column" position="sticky" top="200px" px={[6, 0]}>
              <Box>
                {giftcard?.title && (
                  <Text as="h1" size="3xl" fontSize={{ base: "1.5rem", sm: "3xl" }} color="grey.text" whiteSpace={"pre-wrap"} mb={2}>
                    {giftcard.title}
                  </Text>
                )}

                {giftcard?.giftcardDescription && (
                  <Text size="md" fontSize={16} color="grey.text">
                    {giftcard.giftcardDescription}
                  </Text>
                )}
              </Box>
              <Flex direction="column" alignItems="left">
                {giftcard?.step1 && (
                  <Text size="sm" fontSize={14} mb="1" letterSpacing="generous" color="grey.coal">
                    {giftcard.step1}
                  </Text>
                )}
                <Box mb="10">
                  <Flex width="100%" rowGap="3" gap={[3, 4]} flexWrap="wrap">
                    <ProductGiftCardDenominations
                      selectedOptions={selectedOptions}
                      selectableOptions={selectableOptions}
                      handleOption={handleOption}
                    />
                  </Flex>
                </Box>
                {giftcard?.step1 && (
                  <Text size="sm" fontSize={14} mb="1" letterSpacing="generous" color="grey.coal">
                    {giftcard.step2}
                  </Text>
                )}
                <Box mb="5">
                  <Grid width="full" rowGap="3" gap="4" gridTemplateColumns={["repeat(2, 1fr)"]}>
                    <ProductGiftCardDesigns
                      selectedOptions={selectedOptions}
                      selectableOptions={selectableOptions}
                      handleOption={handleOption}
                      product={product}
                    />
                  </Grid>
                </Box>
              </Flex>
              <ProductGiftcardToggle handleErrors={handleErrors} handleToggle={onToggle} isLoading={isLoading} variant={activeVariant} />
              <ProductGiftcardForm
                isOpen={isOpen}
                handleToggle={onToggle}
                variant={activeVariant}
                selectableOptions={selectableOptions}
                selectedOptions={selectedOptions}
                handleOption={handleOption}
              />
              <ProductGiftcardBalance />
              <>
                <Accordion allowToggle defaultIndex={[0]}>
                  <AccordionItem border="none">
                    <AccordionButton role={!isTouch ? "group" : undefined} color="grey.lake" cursor="pointer" py={4}>
                      <HStack alignItems="center" justifyContent="space-between" w="full">
                        <Text flex={1} size="sm" textDecoration="underline" textUnderlineOffset={3} color="grey.coal">
                          {giftcard?.additionalTermsTitle}
                        </Text>
                      </HStack>
                    </AccordionButton>
                    <AccordionPanel p={0}>
                      <Text flex={1} size="sm">
                        {giftcard?.additionalTermsContent}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </>
            </Flex>
          </VStack>
        </Stack>
      </Container>
      {!isLoading && <RecentlyViewed />}
    </>
  )
})
